import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "../api";
import { useAuth } from "../auth";

function CreateForm({ onCreated }) {
  const [f, setF] = useState({ display_name: "", username: "", email: "", password: "", role: "student" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!f.display_name.trim()) { setMsg({ ok: false, text: "Display name is required" }); return; }
    setMsg(null); setBusy(true);
    try {
      const { data } = await api.post("/admin/users", f);
      setMsg({ ok: true, text: `Created ${data.display_name}` });
      setF({ display_name: "", username: "", email: "", password: "", role: "student" });
      onCreated();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e, "Could not create account") });
    } finally { setBusy(false); }
  };

  return (
    <div className="llh-card">
      <div className="llh-eyebrow llh-mb-12">New account</div>
      <label className="llh-label">Display name</label>
      <input className="llh-input llh-mb-10" value={f.display_name} onChange={set("display_name")} placeholder="The name they're known by at the school" />
      <div className="llh-form-grid">
        <div><label className="llh-label">Username</label><input className="llh-input llh-mb-10" value={f.username} autoCapitalize="none" onChange={set("username")} /></div>
        <div><label className="llh-label">Email</label><input className="llh-input llh-mb-10" value={f.email} autoCapitalize="none" onChange={set("email")} /></div>
        <div><label className="llh-label">Temporary password</label><input className="llh-input llh-mb-10" value={f.password} onChange={set("password")} /></div>
        <div>
          <label className="llh-label">Role</label>
          <select className="llh-input llh-mb-10 llh-h-41" value={f.role} onChange={set("role")}>
            <option value="student">Student</option>
            <option value="admin">Coach (admin)</option>
          </select>
        </div>
      </div>
      <div className="llh-row-center">
        <button className="llh-btn-primary llh-btn--dim6" disabled={busy} onClick={submit}>{busy ? "Creating..." : "Create account"}</button>
        {msg && <span className={msg.ok ? "llh-fs-13 llh-c-done" : "llh-fs-13 llh-c-crimson"}>{msg.text}</span>}
      </div>
      <p className="llh-dim-12 llh-mt-12 llh-mb-0">
        Give the student their username and this temporary password. They can change it after signing in.
      </p>
    </div>
  );
}

function PasswordModal({ username, onClose, onSubmit }) {
  const [pw, setPw] = useState("");
  const [msg, setMsg] = useState(null);
  const submit = async () => {
    if (pw.length < 6) { setMsg("At least 6 characters"); return; }
    try { await onSubmit(pw); onClose(); } catch (e) { setMsg(errMsg(e)); }
  };
  return (
    <div onClick={onClose} className="llh-modal-overlay">
      <div onClick={(e) => e.stopPropagation()} className="llh-modal">
        <div className="llh-eyebrow llh-mb-12">Reset password</div>
        <p className="llh-dim-13 llh-mt-0">Set a new password for <strong className="llh-strong-text">{username}</strong>.</p>
        <input className="llh-input llh-mb-10" type="text" placeholder="New password" value={pw} autoFocus onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
        {msg && <div className="llh-error-text llh-mb-10">{msg}</div>}
        <div className="llh-actions-end llh-mt-6">
          <button className="llh-btn-ghost llh-btn-ghost--sm" onClick={onClose}>Cancel</button>
          <button className="llh-btn-primary" onClick={submit}>Set password</button>
        </div>
      </div>
    </div>
  );
}

function UserRow({ u, me, onChange }) {
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const wrap = (fn) => async () => { setBusy(true); try { await fn(); onChange(); } catch (e) { alert(errMsg(e)); } finally { setBusy(false); } };

  const toggleRole = wrap(async () => {
    const role = u.role === "admin" ? "student" : "admin";
    if (window.confirm(`Make ${u.display_name} a ${role}?`)) await api.put(`/admin/users/${u.id}/role`, { role });
  });
  const del = wrap(async () => {
    if (window.confirm(`Delete ${u.display_name}? This removes all their data and cannot be undone.`)) await api.delete(`/admin/users/${u.id}`);
  });

  const last = u.last_workout ? String(u.last_workout).slice(0, 10) : "never";
  return (
    <div className="llh-card llh-card--tight llh-mb-10">
      <div className="llh-row-top">
        <div>
          <div className="llh-name">
            {u.display_name}
            {u.role === "admin" && <span className="llh-badge llh-badge--brass llh-ml-8">Coach</span>}
            {me && me.id === u.id && <span className="llh-dim-11 llh-ml-8">(you)</span>}
          </div>
          <div className="llh-meta">{u.username} · {u.email}</div>
          <div className="llh-meta">
            {u.session_count} session{Number(u.session_count) === 1 ? "" : "s"} · last {last}
          </div>
        </div>
        <div className="llh-btn-row">
          {u.share_progress && <Link to={`/admin/students/${u.id}`} className="llh-btn-ghost llh-btn-ghost--sm llh-link-reset">View sessions</Link>}
          <button className="llh-btn-ghost llh-btn-ghost--sm" disabled={busy} onClick={() => setPwOpen(true)}>Reset password</button>
          {!(me && me.id === u.id) && <button className="llh-btn-ghost llh-btn-ghost--sm" disabled={busy} onClick={toggleRole}>{u.role === "admin" ? "Make student" : "Make coach"}</button>}
          {!(me && me.id === u.id) && <button className="llh-btn-ghost llh-btn-ghost--sm llh-danger" disabled={busy} onClick={del}>Delete</button>}
        </div>
      </div>
      {pwOpen && <PasswordModal username={u.display_name} onClose={() => setPwOpen(false)}
        onSubmit={(pw) => api.put(`/admin/users/${u.id}/password`, { new_password: pw })} />}
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { setErr(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="llh-page llh-pb-40">
      <header className="llh-header-bar">
        <Link to="/" className="llh-navlink"><span className="llh-nav-caret">‹</span> Tools</Link>
        <div className="llh-header-title">ADMIN PANEL</div>
        <button onClick={logout} className="llh-btn-text">Sign out</button>
      </header>

      <main className="llh-main">
        <Link to="/admin/overview" className="llh-card llh-card-link llh-mb-12">
          <div>
            <div className="llh-name">Coaching overview</div>
            <div className="llh-meta">See who's training and review sessions without the account tools.</div>
          </div>
          <span className="llh-open-arrow">Open →</span>
        </Link>

        <Link to="/admin/programs" className="llh-card llh-card-link llh-mb-16">
          <div>
            <div className="llh-name">Programs</div>
            <div className="llh-meta">Build custom workout rotations and manage the lift library.</div>
          </div>
          <span className="llh-open-arrow">Open →</span>
        </Link>

        <div className="llh-eyebrow">Manage</div>
        <h2 className="llh-h2 llh-mb-16">Students</h2>

        <CreateForm onCreated={load} />

        {err && <div className="llh-error-text llh-mb-12">{err}</div>}
        {users === null ? <div className="llh-spin" /> :
          users.length === 0 ? <div className="llh-dim">No accounts yet.</div> :
          users.map((u) => <UserRow key={u.id} u={u} me={user} onChange={load} />)}
      </main>
    </div>
  );
}
